import { useAuth, type TellescopeModelFromName } from "@tellescope/tanstack-query";
import type { WeeklyAvailability } from "@tellescope/types-models";
import { useCurrentTimestamp } from "@tellescope/ui";
import { useCallback } from "react";

type User = TellescopeModelFromName<"users">;

const isoWeekday = (date: Date, timezone: string) =>
  Number(new Intl.DateTimeFormat("en-US", { timeZone: timezone, weekday: "short" }).format(date));

const DAY_INDEX: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };

const getDayOfWeekInTimezone = (date: Date, timezone: string) => {
  const short = new Intl.DateTimeFormat("en-US", { timeZone: timezone, weekday: "short" }).format(date);
  return DAY_INDEX[short] ?? isoWeekday(date, timezone);
};

const getMinutesOfDayInTimezone = (date: Date, timezone: string) => {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: timezone,
    hour: "numeric",
    minute: "numeric",
    hourCycle: "h23",
  }).formatToParts(date);
  const hour = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
  const minute = Number(parts.find((p) => p.type === "minute")?.value ?? 0);
  return hour * 60 + minute;
};

const isWithinActiveRange = (active: WeeklyAvailability["active"], date: Date) => {
  if (!active) return true;
  const time = date.getTime();
  if (active.from instanceof Date && time < active.from.getTime()) return false;
  if (active.to instanceof Date && time > active.to.getTime()) return false;
  return true;
};

const isBlockActiveNow = (block: WeeklyAvailability, now: Date, timezone: string) => {
  if (block.dayOfWeekStartingSundayIndexedByZero !== getDayOfWeekInTimezone(now, timezone)) return false;
  if (!isWithinActiveRange(block.active, now)) return false;
  const minutesOfDay = getMinutesOfDayInTimezone(now, timezone);
  return minutesOfDay >= block.startTimeInMinutes && minutesOfDay < block.endTimeInMinutes;
};

/**
 * Derives "out of office" purely from `weeklyAvailabilities` — there is no stored OOO field.
 * A user with no configured availabilities is never flagged (mirrors the "no restriction" default
 * used for scheduling availability elsewhere in the app).
 */
export const isUserOutOfOfficeNow = (user: User, fallbackTimezone?: string, now: Date = new Date()): boolean => {
  const availabilities = user.weeklyAvailabilities ?? [];
  if (availabilities.length === 0) return false;
  const timezone = user.timezone || fallbackTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  return !availabilities.some((block) => isBlockActiveNow(block, now, timezone));
};

export const useIsUserOutOfOfficeNow = () => {
  const { selectedOrganization } = useAuth();
  const fallbackTimezone = selectedOrganization?.timezone;
  // Polling (rather than a one-shot `new Date()`) lets callers memoize on this predicate's
  // identity and still pick up an office-hours boundary crossing within a minute.
  const now = useCurrentTimestamp({ pollingRateInMs: 1000 * 60, snapTo: "minute" });
  return useCallback((user: User) => isUserOutOfOfficeNow(user, fallbackTimezone, now), [fallbackTimezone, now]);
};
